import React, { useState } from 'react';

import { Link } from 'react-router-dom';

import { motion } from 'framer-motion';
import {
  ArrowRight,
  Award,
  BookOpen,
  Clock,
  DollarSign,
  ExternalLink,
  Info,
  Target,
  TrendingUp,
} from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { roiServiceSources } from '../data/roiServiceSources';
import ModernButton from './ui/ModernButton';

interface ROIServiceChartProps {
  className?: string;
}

interface ROIDataItem {
  name: string;
  roi: number;
  tiempo: number;
  inversion: string;
  beneficios: string[];
  color?: string;
}

const ROIServiceChart: React.FC<ROIServiceChartProps> = ({ className = '' }) => {
  // Estados para controlar qué métricas se muestran en detalle
  const [activeRoiMetric, setActiveRoiMetric] = useState<string | null>(null);
  const [showRoiExplanation, setShowRoiExplanation] = useState<boolean>(false);
  const [showSources, setShowSources] = useState<boolean>(false);
  const [hoverMetric, setHoverMetric] = useState<string | null>(null);

  // Datos mejorados para el gráfico de barras - ROI por tipo de servicio con colores personalizados
  const roiData: ROIDataItem[] = [
    {
      name: 'Desarrollo Web',
      roi: 320,
      tiempo: 6,
      inversion: 'Media',
      color: '#3B82F6', // Azul
      beneficios: [
        'Mayor visibilidad online',
        'Captación de clientes 24/7',
        'Expansión de mercado',
      ],
    },
    {
      name: 'ERP',
      roi: 420,
      tiempo: 12,
      inversion: 'Alta',
      color: '#10B981', // Verde
      beneficios: ['Optimización de procesos', 'Reducción de errores', 'Mejor toma de decisiones'],
    },
    {
      name: 'Transformación',
      roi: 380,
      tiempo: 9,
      inversion: 'Alta',
      color: '#F59E0B', // Naranja
      beneficios: ['Nuevos canales de ingresos', 'Eficiencia operativa', 'Ventajas competitivas'],
    },
    {
      name: 'Consultoría',
      roi: 290,
      tiempo: 3,
      inversion: 'Media-Baja',
      color: '#8B5CF6', // Púrpura
      beneficios: [
        'Optimización de procesos',
        'Implementación de mejores prácticas',
        'Resultados rápidos',
      ],
    },
    {
      name: 'Capacitación',
      roi: 250,
      tiempo: 2,
      inversion: 'Baja',
      color: '#EC4899', // Rosa
      beneficios: [
        'Aumento de productividad',
        'Reducción de errores',
        'Mayor satisfacción de empleados',
      ],
    },
  ];

  // Función para mostrar información detallada al hacer clic en una métrica
  const handleRoiMetricClick = (metricName: string) => {
    setActiveRoiMetric(activeRoiMetric === metricName ? null : metricName);
  };

  // Información detallada para cada servicio y su ROI
  const roiDetails: Record<string, string> = {
    'Desarrollo Web':
      'El ROI del 320% en Desarrollo Web significa que por cada $1 invertido, se obtienen $3.20 de retorno. Este retorno se materializa en aproximadamente 6 meses y requiere una inversión inicial media. El desarrollo web profesional genera este alto ROI a través de mayor visibilidad online, captación de clientes 24/7 y expansión del mercado potencial.',
    ERP: 'El ROI del 420% en implementación de ERP representa $4.20 de retorno por cada $1 invertido. Aunque requiere una inversión inicial alta y un tiempo de maduración de 12 meses, ofrece el mayor retorno a largo plazo. Este ROI se logra mediante la optimización de procesos, reducción de errores operativos y mejora en la toma de decisiones basada en datos.',
    Transformación:
      'La Transformación Digital ofrece un ROI del 380%, generando $3.80 por cada $1 invertido. Con un tiempo de maduración de 9 meses y una inversión inicial alta, este servicio transforma fundamentalmente los modelos de negocio. El ROI proviene de nuevos canales de ingresos, eficiencia operativa y ventajas competitivas sostenibles.',
    Consultoría:
      'Los servicios de Consultoría generan un ROI del 290%, es decir, $2.90 por cada $1 invertido. Con un tiempo de implementación corto (3 meses) y una inversión inicial media-baja, ofrece resultados rápidos. Este retorno se obtiene a través de la optimización de procesos existentes y la implementación de mejores prácticas.',
    Capacitación:
      'La Capacitación Empresarial proporciona un ROI del 250%, equivalente a $2.50 por cada $1 invertido. Con la inversión inicial más baja y resultados visibles en solo 2 meses, es la opción más accesible. El ROI proviene del aumento en productividad del personal, reducción de errores y mayor satisfacción de empleados.',
  };

  // Función para controlar el estado de hover en métricas
  const handleMetricHover = (metricName: string | null) => {
    setHoverMetric(metricName);
  };

  return (
    <section className={`py-16 ${className}`}>
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">ROI Promedio por Servicio</h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-4">
            Análisis comparativo del retorno de inversión para diferentes servicios tecnológicos,
            basado en datos reales de implementaciones exitosas y estudios de mercado.
          </p>
          <p className="text-md text-gray-700 max-w-2xl mx-auto italic border-l-4 border-blue-400 pl-4 py-2 bg-blue-50">
            Nuestros servicios exclusivos generan un ROI excepcional, con retornos que superan
            ampliamente la inversión inicial. Cada servicio está diseñado para maximizar el valor
            empresarial a través de soluciones personalizadas de alta calidad.
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-6 mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-gradient-to-br from-gray-50 to-blue-50 p-6 rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 border border-gray-100"
          >
            <div className="flex justify-between items-center mb-6">
              <h4 className="text-xl font-semibold text-gray-800 flex items-center">
                <DollarSign className="h-5 w-5 mr-2 text-blue-600" />
                ROI Promedio por Servicio (%)
              </h4>
              <div className="flex space-x-2">
                <button
                  onClick={() => setShowRoiExplanation(!showRoiExplanation)}
                  className="px-3 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full flex items-center hover:bg-blue-200 transition-colors"
                >
                  <Info className="h-3 w-3 mr-1" />
                  {showRoiExplanation ? 'Ocultar explicación' : '¿Qué significa el ROI?'}
                </button>
                <button
                  onClick={() => setShowSources(!showSources)}
                  className="px-3 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full flex items-center hover:bg-blue-200 transition-colors"
                >
                  <BookOpen className="h-3 w-3 mr-1" />
                  {showSources ? 'Ocultar fuentes' : 'Ver fuentes de datos'}
                </button>
                <span className="px-3 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full flex items-center">
                  <DollarSign className="h-3 w-3 mr-1" /> Datos validados 2023
                </span>
              </div>
            </div>

            {showRoiExplanation && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-100 text-sm"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                  <div>
                    <h6 className="font-medium text-blue-800 mb-1">¿Qué es el ROI?</h6>
                    <p className="text-blue-700 mb-2">
                      El <strong>Retorno sobre la Inversión (ROI)</strong> es una métrica financiera
                      que mide la rentabilidad de una inversión. Se expresa como un porcentaje que
                      representa la ganancia neta en relación al costo de la inversión.
                    </p>

                    <h6 className="font-medium text-blue-800 mb-1">Cómo calculamos el ROI</h6>
                    <p className="text-blue-700 mb-2">
                      ROI = (Beneficio - Inversión) / Inversión × 100%
                    </p>

                    <p className="text-blue-700 mb-2">
                      Por ejemplo, un ROI del 320% significa que por cada $1 invertido, se obtiene
                      un retorno de $3.20 adicionales.
                    </p>
                  </div>

                  <div>
                    <h6 className="font-medium text-blue-800 mb-1">
                      Factores que influyen en el ROI
                    </h6>
                    <ul className="text-blue-700 space-y-2">
                      <li>
                        <strong>• Tiempo de maduración:</strong> Algunos servicios requieren más
                        tiempo para alcanzar su máximo potencial.
                      </li>
                      <li>
                        <strong>• Inversión inicial:</strong> Servicios con mayor inversión inicial
                        suelen ofrecer mayor ROI a largo plazo.
                      </li>
                      <li>
                        <strong>• Alcance de implementación:</strong> La profundidad de la
                        implementación afecta directamente el retorno.
                      </li>
                      <li>
                        <strong>• Compromiso organizacional:</strong> El nivel de adopción interna
                        impacta significativamente los resultados.
                      </li>
                      <li>
                        <strong>• Mantenimiento continuo:</strong> La optimización constante
                        maximiza el ROI a lo largo del tiempo.
                      </li>
                    </ul>
                  </div>
                </div>

                <p className="text-blue-700">
                  <strong>Interpretación:</strong> Cuanto mayor sea el porcentaje, más rentable es
                  la inversión. Los servicios con ROI más alto generan mayor valor a largo plazo,
                  aunque pueden requerir mayor inversión inicial o tiempo de maduración.
                </p>
              </motion.div>
            )}

            {/* Panel de fuentes de datos */}
            {showSources && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
                className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-100"
              >
                <div className="flex items-center justify-between mb-3">
                  <h5 className="text-sm font-semibold text-blue-800 flex items-center">
                    <Info className="h-4 w-4 mr-1" /> Fuentes de datos utilizadas
                  </h5>
                  <button
                    onClick={() => setShowSources(false)}
                    className="text-blue-500 hover:text-blue-700 text-xs"
                  >
                    Cerrar
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {roiServiceSources.map((source, index) => (
                    <div
                      key={index}
                      className="bg-white p-3 rounded-md shadow-sm border border-blue-100"
                    >
                      <div className="flex justify-between items-start">
                        <h6 className="text-sm font-medium text-gray-800">{source.name}</h6>
                        <span className="bg-blue-100 text-blue-800 text-xs px-2 py-0.5 rounded">
                          {source.year}
                        </span>
                      </div>
                      <p className="text-xs text-gray-600 mt-1 mb-2">{source.description}</p>
                      <div className="flex justify-between items-center">
                        <span className="text-xs text-gray-500">{source.organization}</span>
                        <a
                          href={source.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-blue-600 hover:text-blue-800 flex items-center"
                        >
                          Verificar fuente <ExternalLink className="h-3 w-3 ml-1" />
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}

            <div className="h-96">
              {' '}
              {/* Altura aumentada para mejor visualización */}
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={roiData}
                  margin={{ top: 10, right: 30, left: 20, bottom: 10 }}
                  barSize={40}
                  onMouseMove={data => {
                    if (data && data.activeLabel) {
                      handleMetricHover(data.activeLabel);
                    }
                  }}
                  onMouseLeave={() => handleMetricHover(null)}
                >
                  <defs>
                    {roiData.map((entry, index) => (
                      <linearGradient
                        key={`gradient-${index}`}
                        id={`colorRoi${index}`}
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop offset="5%" stopColor={entry.color} stopOpacity={0.9} />
                        <stop offset="95%" stopColor={entry.color} stopOpacity={0.7} />
                      </linearGradient>
                    ))}
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis
                    dataKey="name"
                    tick={{ fill: '#4B5563', fontWeight: 500 }}
                    axisLine={{ stroke: '#E5E7EB' }}
                  />
                  <YAxis
                    domain={[0, 500]}
                    tick={{ fill: '#4B5563' }}
                    axisLine={{ stroke: '#E5E7EB' }}
                    tickFormatter={value => `${value}%`}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'rgba(255, 255, 255, 0.95)',
                      borderRadius: '8px',
                      border: '1px solid #E5E7EB',
                      boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                    }}
                    formatter={(value, name, props) => {
                      if (name === 'roi') {
                        const item = roiData.find(d => d.name === props.payload.name);
                        return [
                          <div>
                            <div className="font-medium text-lg">{value}%</div>
                            <div className="grid grid-cols-2 gap-2 mt-2">
                              <div>
                                <div className="text-xs text-gray-500">Tiempo hasta ROI:</div>
                                <div className="text-sm font-medium">{item?.tiempo} meses</div>
                              </div>
                              <div>
                                <div className="text-xs text-gray-500">Inversión inicial:</div>
                                <div className="text-sm font-medium">{item?.inversion}</div>
                              </div>
                              <div>
                                <div className="text-xs text-gray-500">Por cada $1 invertido:</div>
                                <div className="text-sm font-medium text-green-600">
                                  ${((value as number) / 100).toFixed(2)}
                                </div>
                              </div>
                            </div>
                          </div>,
                          'ROI',
                        ];
                      }
                      return [value, name];
                    }}
                    cursor={{ fill: 'rgba(0, 0, 0, 0.05)' }}
                    labelFormatter={name => (
                      <span className="font-medium text-gray-800">Servicio: {name}</span>
                    )}
                  />
                  <Legend
                    verticalAlign="top"
                    height={36}
                    wrapperStyle={{ paddingTop: '10px' }}
                    formatter={() => (
                      <span className="text-sm font-medium">Retorno sobre Inversión</span>
                    )}
                  />
                  <Bar
                    dataKey="roi"
                    animationDuration={1800}
                    animationEasing="ease-in-out"
                    onClick={data => handleRoiMetricClick(data.name)}
                    radius={[4, 4, 0, 0]}
                    activeBar={{ stroke: '#2563EB', strokeWidth: 2 }}
                  >
                    {roiData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={`url(#colorRoi${index})`}
                        stroke={
                          hoverMetric === entry.name || activeRoiMetric === entry.name
                            ? '#fff'
                            : 'transparent'
                        }
                        strokeWidth={
                          hoverMetric === entry.name || activeRoiMetric === entry.name ? 2 : 0
                        }
                      />
                    ))}
                    <LabelList
                      dataKey="roi"
                      position="top"
                      formatter={(value: number) => `${value}%`}
                      style={{ fill: '#4B5563', fontWeight: 'bold', fontSize: 12 }}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-3 text-sm text-gray-600 text-center bg-blue-50 p-3 rounded-lg border border-blue-100">
              <p className="flex items-center justify-center mb-2">
                <Target className="h-4 w-4 mr-1 text-blue-600" />
                Haga clic en las barras para ver detalles completos sobre cada servicio
              </p>
              <p className="text-xs text-gray-700 max-w-3xl mx-auto pt-2 border-t border-blue-100">
                Este análisis de ROI demuestra el valor excepcional de nuestras soluciones premium.
                Cada servicio ha sido meticulosamente diseñado para maximizar el retorno financiero,
                optimizando procesos críticos y generando ventajas competitivas sostenibles.
                Nuestros clientes exclusivos experimentan beneficios tangibles que superan
                significativamente la inversión inicial.
              </p>
            </div>

            {/* Panel de información detallada para ROI */}
            {activeRoiMetric && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
                className="mt-6 p-5 bg-white rounded-lg border border-gray-200 shadow-sm"
              >
                <div className="flex items-start">
                  <div
                    className="h-10 w-10 rounded-full flex items-center justify-center mr-4"
                    style={{
                      backgroundColor:
                        roiData.find(item => item.name === activeRoiMetric)?.color + '20',
                    }}
                  >
                    <DollarSign
                      className="h-6 w-6"
                      style={{ color: roiData.find(item => item.name === activeRoiMetric)?.color }}
                    />
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between items-center mb-2">
                      <h5 className="text-lg font-semibold text-gray-800">
                        {activeRoiMetric} - ROI{' '}
                        {roiData.find(item => item.name === activeRoiMetric)?.roi}%
                      </h5>
                      <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full flex items-center">
                        <Award className="h-3 w-3 mr-1" /> Alto rendimiento
                      </span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3 mb-4">
                      <div className="bg-blue-50 p-3 rounded-lg border border-blue-100">
                        <div className="flex items-center mb-1">
                          <Clock className="h-4 w-4 text-blue-600 mr-1" />
                          <span className="text-xs text-blue-700 font-medium">
                            Tiempo hasta ROI:
                          </span>
                        </div>
                        <p className="text-sm font-medium">
                          {roiData.find(item => item.name === activeRoiMetric)?.tiempo} meses
                        </p>
                      </div>
                      <div className="bg-green-50 p-3 rounded-lg border border-green-100">
                        <div className="flex items-center mb-1">
                          <DollarSign className="h-4 w-4 text-green-600 mr-1" />
                          <span className="text-xs text-green-700 font-medium">
                            Inversión inicial:
                          </span>
                        </div>
                        <p className="text-sm font-medium">
                          {roiData.find(item => item.name === activeRoiMetric)?.inversion}
                        </p>
                      </div>
                      <div className="bg-purple-50 p-3 rounded-lg border border-purple-100">
                        <div className="flex items-center mb-1">
                          <TrendingUp className="h-4 w-4 text-purple-600 mr-1" />
                          <span className="text-xs text-purple-700 font-medium">
                            Por cada $1 invertido:
                          </span>
                        </div>
                        <p className="text-sm font-medium">
                          $
                          {(
                            (roiData.find(item => item.name === activeRoiMetric)?.roi || 0) / 100
                          ).toFixed(2)}
                        </p>
                      </div>
                    </div>

                    <div className="mb-4">
                      <h6 className="text-sm font-medium text-gray-700 mb-2">
                        Descripción detallada:
                      </h6>
                      <p className="text-sm text-gray-600 leading-relaxed">
                        {roiDetails[activeRoiMetric]}
                      </p>
                    </div>

                    <div>
                      <h6 className="text-sm font-medium text-gray-700 mb-2">
                        Principales beneficios:
                      </h6>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                        {roiData
                          .find(item => item.name === activeRoiMetric)
                          ?.beneficios.map((beneficio, index) => (
                            <div
                              key={index}
                              className="bg-gray-50 p-2 rounded-lg border border-gray-100 text-sm text-gray-700"
                            >
                              • {beneficio}
                            </div>
                          ))}
                      </div>
                    </div>

                    <div className="mt-4 pt-3 border-t border-gray-100 flex justify-between items-center">
                      <div className="text-xs text-gray-500">Fuente: Datos validados 2023</div>
                      <button
                        onClick={() => setActiveRoiMetric(null)}
                        className="text-blue-600 hover:text-blue-800 text-sm"
                      >
                        Cerrar detalles
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </motion.div>
        </div>

        {/* CTA estratégico después del gráfico de ROI */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="mt-16 text-center"
        >
          <div className="max-w-3xl mx-auto bg-gradient-to-r from-blue-50 to-indigo-50 p-8 rounded-xl shadow-md border border-blue-100">
            <h3 className="text-2xl font-bold text-gray-800 mb-4">
              Maximice su Retorno de Inversión
            </h3>
            <p className="text-gray-600 mb-6">
              Nuestras soluciones exclusivas están diseñadas para generar resultados excepcionales y
              un ROI superior. Descubra cómo podemos potenciar su inversión.
            </p>
            <Link to="/servicios" onClick={() => window.scrollTo(0, 0)}>
              <ModernButton variant="primary" size="md" className="shadow-md">
                Explorar Soluciones Premium
                <ArrowRight className="ml-2 h-5 w-5" />
              </ModernButton>
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ROIServiceChart;
